import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Clock3, Eye, Hash, LoaderCircle, PencilLine, Plus, Trash2 } from 'lucide-react';
import {
  createFormSubmissionApi,
  deleteFormSubmissionApi,
  fetchFormSubmissionsApi,
  fetchProjectFormsApi,
  saveProjectFormWithSchemaApi,
  updateFormSubmissionApi
} from '../../api/formsAdmin.api';
import { fetchProjectEntityApi } from '../../api/entitiesAdmin.api';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { formatJalaliDateTime } from '../../utils/jalaliDate';
import { emptySchemaField, fieldsFromEntity, serializeSchemaFields } from './EntitySchemaEditor';
import { FormEditModal } from './FormEditModal';
import { SubmissionEditModal } from './SubmissionEditModal';
import {
  buildFieldLabels,
  extractFieldColumns,
  getEntityFields,
  getFormIdentity,
  getJsonDataFields,
  getSubmissionFieldValue,
  getSubmissionJsonData,
  MAX_TABLE_FIELD_COLUMNS,
  pickFirst,
  submissionFieldsToObject
} from './requestsUtils';

const PAGE_SIZE = 20;

const getSubmissionNumber = (page, index) => (page - 1) * PAGE_SIZE + index + 1;

export const FormSubmissionsPanel = ({
  projectId,
  formId,
  initialForm = null,
  forceLoading = false,
  disableFetch = false
}) => {
  const { t, language } = useI18n();
  const [form, setForm] = useState(initialForm);
  const [submissions, setSubmissions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [metaError, setMetaError] = useState('');
  const [submissionsError, setSubmissionsError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createValues, setCreateValues] = useState({});
  const [createCustomFields, setCreateCustomFields] = useState([{ slug: '', value: '' }]);
  const [creatingSubmission, setCreatingSubmission] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingSubmission, setDeletingSubmission] = useState(false);
  const [activeSubmission, setActiveSubmission] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [editFieldLabels, setEditFieldLabels] = useState({});
  const [detailsSubmission, setDetailsSubmission] = useState(null);
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [formEditOpen, setFormEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ displayName: '', authNeed: false, isPrivate: false });
  const [schemaFields, setSchemaFields] = useState([emptySchemaField()]);
  const [updatingForm, setUpdatingForm] = useState(false);
  const [formEditError, setFormEditError] = useState('');

  const schemaColumns = useMemo(
    () =>
      getEntityFields(form)
        .filter((field) => field?.slug || field?.Slug)
        .map((field) => ({
          slug: field.slug || field.Slug,
          name: field.name || field.Name || field.slug || field.Slug,
          type: field.type || field.Type
        })),
    [form]
  );
  const columns = schemaColumns.length ? schemaColumns.slice(0, MAX_TABLE_FIELD_COLUMNS) : extractFieldColumns(submissions);
  const createColumns = schemaColumns.length ? schemaColumns : extractFieldColumns(submissions, 100);
  const detailsFields = useMemo(() => getJsonDataFields(getSubmissionJsonData(detailsSubmission)), [detailsSubmission]);
  const tableMinWidth = `${columns.length * 170 + 520}px`;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const PrevIcon = language === 'fa' ? ChevronRight : ChevronLeft;
  const NextIcon = language === 'fa' ? ChevronLeft : ChevronRight;
  const formIdentity = getFormIdentity(form);
  const formTitle = form?.displayName || form?.DisplayName || formIdentity.slug || t('requestsUntitledForm');

  const loadFormMeta = useCallback(async () => {
    setLoadingMeta(true);
    setMetaError('');

    try {
      const forms = await fetchProjectFormsApi(projectId);
      const matchedForm = forms.find((item) => getFormIdentity(item).id === formId) || null;

      if (!matchedForm) {
        throw new Error(t('requestsFormNotFound'));
      }

      let nextForm = matchedForm;
      const slug = getFormIdentity(matchedForm).slug;
      if (slug) {
        try {
          const entity = await fetchProjectEntityApi({ projectId, entity: slug });
          const fields = getEntityFields(entity);
          nextForm = {
            ...matchedForm,
            id: getFormIdentity(matchedForm).id,
            slug,
            hasSchema: Boolean(entity?.hasSchema ?? entity?.HasSchema) || fields.length > 0,
            fields
          };
        } catch {
          nextForm = { ...matchedForm, id: getFormIdentity(matchedForm).id, slug };
        }
      }

      setForm(nextForm);
    } catch (error) {
      setForm(null);
      setMetaError(error?.message || t('requestsFormsError'));
    } finally {
      setLoadingMeta(false);
    }
  }, [formId, projectId, t]);

  const loadSubmissions = useCallback(async () => {
    if (!formId) {
      setSubmissions([]);
      setTotalCount(0);
      return;
    }

    setLoadingSubmissions(true);
    setSubmissionsError('');

    try {
      const result = await fetchFormSubmissionsApi({
        projectId,
        formId,
        page,
        pageSize: PAGE_SIZE
      });
      setSubmissions(
        (result.items || []).map((item) => ({
          ...item,
          id: pickFirst(item, 'id', 'Id') || item.id,
          createdAt: pickFirst(item, 'createdAt', 'CreatedAt') || item.createdAt,
          jsonData: getSubmissionJsonData(item)
        }))
      );
      setTotalCount(result.totalCount);
    } catch (error) {
      setSubmissions([]);
      setTotalCount(0);
      setSubmissionsError(error?.message || t('requestsSubmissionsError'));
    } finally {
      setLoadingSubmissions(false);
    }
  }, [formId, page, projectId, t]);

  useEffect(() => {
    if (!projectId || !formId || disableFetch) {
      setLoadingMeta(Boolean(projectId && formId && disableFetch));
      return;
    }

    if (getFormIdentity(initialForm).id === formId) {
      setForm(initialForm);
      setLoadingMeta(false);
      return;
    }

    loadFormMeta();
  }, [disableFetch, formId, initialForm, loadFormMeta, projectId]);

  useEffect(() => {
    setPage(1);
  }, [formId]);

  useEffect(() => {
    if (!projectId || !formId || disableFetch || loadingMeta || metaError) {
      return;
    }

    loadSubmissions();
  }, [disableFetch, formId, loadSubmissions, loadingMeta, metaError, projectId]);

  const openEditForm = () => {
    const entityFields = getEntityFields(form);
    const inferred = fieldsFromEntity(entityFields);

    setEditForm({
      displayName: form?.displayName || form?.DisplayName || '',
      authNeed: Boolean(form?.authNeed ?? form?.AuthNeed),
      isPrivate: Boolean(form?.isPrivate ?? form?.IsPrivate)
    });
    setSchemaFields(inferred);
    setFormEditError('');
    setFormEditOpen(true);
    setActionError('');
    setActionSuccess('');
  };

  const handleUpdateForm = async (event) => {
    event.preventDefault();

    if (!form || updatingForm) {
      return;
    }

    const identity = getFormIdentity(form);
    if (!identity.id) {
      setFormEditError(t('requestsUpdateFormError'));
      return;
    }

    setUpdatingForm(true);
    setFormEditError('');

    try {
      await saveProjectFormWithSchemaApi({
        projectId,
        formId: identity.id,
        slug: identity.slug,
        displayName: editForm.displayName.trim() || form.displayName || form.DisplayName || identity.slug,
        authNeed: editForm.authNeed,
        isPrivate: editForm.isPrivate,
        fields: serializeSchemaFields(schemaFields)
      });
      setFormEditOpen(false);
      setActionSuccess(t('requestsUpdateFormSuccess'));
      await loadFormMeta();
    } catch (updateErr) {
      setFormEditError(updateErr?.message || t('requestsUpdateFormError'));
    } finally {
      setUpdatingForm(false);
    }
  };

  const openEditModal = (submission) => {
    const jsonData = getSubmissionJsonData(submission);
    setActiveSubmission(submission);
    setEditValues(submissionFieldsToObject(jsonData));
    setEditFieldLabels(buildFieldLabels(jsonData));
    setActionError('');
    setActionSuccess('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    if (savingSubmission) {
      return;
    }

    setEditModalOpen(false);
    setActiveSubmission(null);
    setEditValues({});
    setEditFieldLabels({});
    setActionError('');
  };

  const handleFieldChange = (slug, value) => {
    setEditValues((current) => ({
      ...current,
      [slug]: value
    }));
  };

  const handleSaveSubmission = async (event) => {
    event.preventDefault();

    if (!activeSubmission || !formId || savingSubmission) {
      return;
    }

    setSavingSubmission(true);
    setActionError('');
    setActionSuccess('');

    try {
      await updateFormSubmissionApi({
        projectId,
        formId,
        submissionId: activeSubmission.id,
        jsonData: editValues
      });
      setActionSuccess(t('requestsUpdateSuccess'));
      await loadSubmissions();
      window.setTimeout(closeEditModal, 450);
    } catch (error) {
      setActionError(error?.message || t('requestsUpdateError'));
    } finally {
      setSavingSubmission(false);
    }
  };

  const openCreateModal = () => {
    const template = createColumns.reduce((accumulator, column) => {
      accumulator[column.slug] = '';
      return accumulator;
    }, {});

    setCreateValues(template);
    setCreateCustomFields(createColumns.length ? [] : [{ slug: '', value: '' }]);
    setActionError('');
    setActionSuccess('');
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (creatingSubmission) {
      return;
    }

    setCreateModalOpen(false);
    setCreateValues({});
    setCreateCustomFields([{ slug: '', value: '' }]);
    setActionError('');
  };

  const handleCreateValueChange = (slug, value) => {
    setCreateValues((current) => ({
      ...current,
      [slug]: value
    }));
  };

  const handleCustomFieldChange = (index, key, value) => {
    setCreateCustomFields((current) =>
      current.map((field, fieldIndex) => (
        fieldIndex === index
          ? { ...field, [key]: value }
          : field
      ))
    );
  };

  const addCustomField = () => {
    setCreateCustomFields((current) => [...current, { slug: '', value: '' }]);
  };

  const removeCustomField = (index) => {
    setCreateCustomFields((current) => current.filter((_, fieldIndex) => fieldIndex !== index));
  };

  const handleCreateSubmission = async (event) => {
    event.preventDefault();

    if (!formId || creatingSubmission) {
      return;
    }

    const jsonData = { ...createValues };

    createCustomFields.forEach((field) => {
      const slug = field.slug.trim();

      if (slug) {
        jsonData[slug] = field.value;
      }
    });

    if (!Object.keys(jsonData).length) {
      setActionError(t('requestsCreateNoFields'));
      return;
    }

    setCreatingSubmission(true);
    setActionError('');
    setActionSuccess('');

    try {
      await createFormSubmissionApi({ projectId, formId, jsonData });
      setActionSuccess(t('requestsCreateSubmissionSuccess'));
      setPage(1);
      await loadSubmissions();
      window.setTimeout(closeCreateModal, 450);
    } catch (error) {
      setActionError(error?.message || t('requestsCreateSubmissionError'));
    } finally {
      setCreatingSubmission(false);
    }
  };

  const handleDeleteSubmission = async () => {
    if (!deleteTarget || deletingSubmission) {
      return;
    }

    setDeletingSubmission(true);
    setActionError('');
    setActionSuccess('');

    try {
      await deleteFormSubmissionApi({
        projectId,
        formId,
        submissionId: deleteTarget.id
      });
      setDeleteTarget(null);
      setActionSuccess(t('requestsDeleteSubmissionSuccess'));
      await loadSubmissions();
    } catch (error) {
      setActionError(error?.message || t('requestsDeleteSubmissionError'));
    } finally {
      setDeletingSubmission(false);
    }
  };

  if (forceLoading || loadingMeta) {
    return (
      <section className="rounded-xl border border-neutral-200 bg-white p-8 dark:border-neutral-800 dark:bg-neutral-950">
        <div className="flex min-h-[320px] items-center justify-center gap-3 text-neutral-500">
          <LoaderCircle className="animate-spin" size={20} />
          <span>{t('requestsLoadingSubmissions')}</span>
        </div>
      </section>
    );
  }

  if (metaError || !form) {
    return (
      <section className="rounded-xl border border-neutral-200 bg-white p-8 dark:border-neutral-800 dark:bg-neutral-950">
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
          <p className="text-sm text-rose-600 dark:text-rose-300">{metaError || t('requestsFormNotFound')}</p>
          <Button type="button" variant="secondary" onClick={loadFormMeta}>
            {t('requestsRetry')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
        <div className="border-b border-neutral-100 px-4 py-5 text-start dark:border-neutral-900 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-neutral-950 dark:text-white">{formTitle}</h1>
              <p className="mt-1 text-sm text-neutral-500">
                {t('requestsFormSubmissionsHint', { count: totalCount, slug: formIdentity.slug })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" onClick={openEditForm}>
                <PencilLine size={16} />
                {t('requestsEditForm')}
              </Button>
              <Button type="button" onClick={openCreateModal}>
                <Plus size={16} />
                {t('requestsCreateSubmission')}
              </Button>
            </div>
          </div>
        </div>

        {loadingSubmissions ? (
          <div className="flex min-h-[280px] items-center justify-center gap-3 px-4 py-10 text-neutral-500">
            <LoaderCircle className="animate-spin" size={18} />
            <span>{t('requestsLoadingSubmissions')}</span>
          </div>
        ) : submissionsError ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-4 py-10 text-center">
            <p className="text-sm text-rose-600 dark:text-rose-300">{submissionsError}</p>
            <Button type="button" variant="secondary" onClick={loadSubmissions}>
              {t('requestsRetry')}
            </Button>
          </div>
        ) : !submissions.length ? (
          <div className="flex min-h-[280px] items-center justify-center px-4 py-10 text-sm text-neutral-500">
            {t('requestsNoSubmissionsForForm')}
          </div>
        ) : (
          <>
            <div className="grid gap-3 px-4 pb-4 pt-4 sm:px-6 lg:hidden">
              {submissions.map((submission, index) => {
                const submissionNumber = getSubmissionNumber(page, index);

                return (
                  <article
                    key={submission.id}
                    className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                          <Hash size={13} />
                          {submissionNumber}
                        </p>
                        <p className="mt-2 break-all text-xs text-neutral-400">{submission.id}</p>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-neutral-400">
                        <Clock3 size={13} />
                        {formatJalaliDateTime(submission.createdAt, language)}
                      </span>
                    </div>

                    <div className="mt-4 grid gap-2">
                      {columns.length ? (
                        columns.slice(0, 3).map((column) => (
                          <div
                            key={`${submission.id}-${column.slug}-card`}
                            className="rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2.5 dark:border-neutral-900 dark:bg-neutral-900"
                          >
                            <p className="text-[11px] font-medium text-neutral-400">{column.name}</p>
                            <p className="mt-1 max-h-12 overflow-hidden break-words text-sm leading-6 text-neutral-950 dark:text-white">
                              {getSubmissionFieldValue(submission, column.slug)}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-lg border border-dashed border-neutral-200 px-3 py-4 text-center text-sm text-neutral-500 dark:border-neutral-800">
                          {t('requestsNoFieldsToShow')}
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-1">
                      <button
                        type="button"
                        className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-900"
                        onClick={() => setDetailsSubmission(submission)}
                      >
                        <Eye size={14} />
                        {t('requestsViewAllFields')}
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                        onClick={() => openEditModal(submission)}
                      >
                        <PencilLine size={15} />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                        onClick={() => setDeleteTarget(submission)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="hidden w-full max-w-full min-w-0 lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-start text-sm" style={{ minWidth: tableMinWidth }}>
                  <thead className="bg-neutral-50 dark:bg-neutral-900">
                    <tr className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-400">
                      <th className="px-4 py-3 text-start">#</th>
                      {columns.map((column) => (
                        <th key={column.slug} className="px-4 py-3 text-start">
                          {column.name}
                        </th>
                      ))}
                      <th className="px-4 py-3 text-start">
                        {t('requestsSubmissionDateJalali')}
                      </th>
                      <th className="sticky end-0 bg-neutral-50 px-4 py-3 text-end dark:bg-neutral-900">
                        {t('requestsActions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission, index) => (
                      <tr
                        key={submission.id}
                        className="border-t border-neutral-100 transition hover:bg-neutral-50 dark:border-neutral-900 dark:hover:bg-neutral-900/60"
                      >
                        <td className="px-4 py-3 align-middle">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-neutral-100 text-xs font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                              {getSubmissionNumber(page, index)}
                            </span>
                            <span className="max-w-[120px] truncate text-xs text-neutral-400">{submission.id}</span>
                          </div>
                        </td>
                        {columns.map((column) => (
                          <td
                            key={`${submission.id}-${column.slug}`}
                            className="px-4 py-3 align-middle text-neutral-950 dark:text-white"
                          >
                            <span className="block max-h-12 min-w-[140px] max-w-[260px] overflow-hidden break-words leading-6">
                              {getSubmissionFieldValue(submission, column.slug)}
                            </span>
                          </td>
                        ))}
                        <td className="px-4 py-3 align-middle text-xs text-neutral-500">
                          {formatJalaliDateTime(submission.createdAt, language)}
                        </td>
                        <td className="sticky end-0 bg-white px-4 py-3 align-middle dark:bg-neutral-950">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              aria-label={t('requestsViewAllFields')}
                              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-neutral-600 transition hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                              onClick={() => setDetailsSubmission(submission)}
                            >
                              <Eye size={14} />
                              {t('requestsViewAllFields')}
                            </button>
                            <button
                              type="button"
                              aria-label={t('requestsEditSubmission')}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                              onClick={() => openEditModal(submission)}
                            >
                              <PencilLine size={15} />
                            </button>
                            <button
                              type="button"
                              aria-label={t('requestsDeleteSubmission')}
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
                              onClick={() => setDeleteTarget(submission)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 ? (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 px-4 py-4 dark:border-neutral-900 sm:px-6">
                <p className="text-xs text-neutral-400">
                  {t('requestsPagination', { page, totalPages, totalCount })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    className="px-3 py-2"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    <PrevIcon size={16} />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="px-3 py-2"
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  >
                    <NextIcon size={16} />
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>

      <FormEditModal
        open={formEditOpen}
        onClose={() => {
          if (!updatingForm) {
            setFormEditOpen(false);
            setFormEditError('');
          }
        }}
        formState={editForm}
        onFormChange={(patch) => setEditForm((current) => ({ ...current, ...patch }))}
        fields={schemaFields}
        onFieldsChange={setSchemaFields}
        onSubmit={handleUpdateForm}
        saving={updatingForm}
        error={formEditError}
        t={t}
      />

      <SubmissionEditModal
        open={editModalOpen}
        onClose={closeEditModal}
        fieldLabels={editFieldLabels}
        editValues={editValues}
        onChange={handleFieldChange}
        onSubmit={handleSaveSubmission}
        saving={savingSubmission}
        error={actionError}
        success={actionSuccess}
      />

      <Modal
        open={createModalOpen}
        onClose={closeCreateModal}
        title={t('requestsCreateSubmissionTitle')}
        closeDisabled={creatingSubmission}
        panelClassName="flex max-h-[min(90vh,720px)] max-w-xl flex-col overflow-hidden p-0"
        contentClassName="mb-0 shrink-0 border-b border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5"
        titleClassName="text-start text-base"
      >
        <form className="flex min-h-0 flex-1 flex-col text-start" onSubmit={handleCreateSubmission}>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3 sm:px-5">
            {createColumns.length ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {createColumns.map((column) => (
                  <Input
                    key={`create-${column.slug}`}
                    label={column.name}
                    value={createValues[column.slug] ?? ''}
                    disabled={creatingSubmission}
                    className="rounded-xl px-3 py-2.5 text-start"
                    onChange={(event) => handleCreateValueChange(column.slug, event.target.value)}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-neutral-200 px-3 py-3 dark:border-neutral-800">
                <p className="text-sm font-semibold text-neutral-950 dark:text-white">{t('requestsCreateCustomFieldsTitle')}</p>
                <p className="mt-1 text-xs leading-6 text-neutral-500">{t('requestsCreateCustomFieldsHint')}</p>
              </div>
            )}

            {createCustomFields.length ? (
              <div className="space-y-3">
                {createCustomFields.map((field, index) => (
                  <div
                    key={`custom-field-${index}`}
                    className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950"
                  >
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                      <Input
                        label={t('requestsCreateFieldName')}
                        value={field.slug}
                        disabled={creatingSubmission}
                        dir="ltr"
                        className="rounded-xl px-3 py-2.5 text-left"
                        onChange={(event) => handleCustomFieldChange(index, 'slug', event.target.value)}
                      />
                      <Input
                        label={t('requestsCreateFieldValue')}
                        value={field.value}
                        disabled={creatingSubmission}
                        className="rounded-xl px-3 py-2.5 text-start"
                        onChange={(event) => handleCustomFieldChange(index, 'value', event.target.value)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-11 px-3"
                        disabled={creatingSubmission || createCustomFields.length <= 1}
                        onClick={() => removeCustomField(index)}
                      >
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full justify-center"
                  disabled={creatingSubmission}
                  onClick={addCustomField}
                >
                  <Plus size={16} />
                  {t('requestsCreateAddField')}
                </Button>
              </div>
            ) : null}

            {actionError ? (
              <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
                {actionError}
              </p>
            ) : null}
            {actionSuccess ? (
              <p className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                {actionSuccess}
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-neutral-100 px-4 py-3 dark:border-neutral-900 sm:px-5">
            <Button type="button" variant="secondary" onClick={closeCreateModal} disabled={creatingSubmission}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={creatingSubmission}>
              {creatingSubmission ? t('requestsCreatingSubmission') : t('create')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deletingSubmission) {
            setDeleteTarget(null);
          }
        }}
        title={t('requestsDeleteSubmissionTitle')}
        closeDisabled={deletingSubmission}
      >
        <div className="space-y-4 text-start">
          <p className="text-sm text-neutral-500">{t('requestsDeleteSubmissionConfirm')}</p>
          {actionError ? <p className="text-sm text-rose-600 dark:text-rose-300">{actionError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deletingSubmission}>
              {t('cancel')}
            </Button>
            <Button type="button" variant="danger" onClick={handleDeleteSubmission} disabled={deletingSubmission}>
              {deletingSubmission ? t('requestsDeletingSubmission') : t('delete')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(detailsSubmission)}
        onClose={() => setDetailsSubmission(null)}
        title={t('requestsAllFieldsTitle')}
        panelClassName="flex max-h-[min(90vh,760px)] max-w-3xl flex-col overflow-hidden p-0"
        contentClassName="mb-0 shrink-0 border-b border-neutral-100 px-5 py-4 dark:border-neutral-900 sm:px-6"
        titleClassName="text-start text-base"
      >
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-start sm:px-6">
          <div className="mb-4 grid gap-3 rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">ID</p>
              <p className="mt-1 break-all text-sm font-medium text-neutral-950 dark:text-white">{detailsSubmission?.id || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
                {t('requestsSubmissionDateJalali')}
              </p>
              <p className="mt-1 text-sm font-medium text-neutral-950 dark:text-white">
                {formatJalaliDateTime(detailsSubmission?.createdAt, language)}
              </p>
            </div>
          </div>

          {detailsFields.length ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {detailsFields.map((field) => (
                <div
                  key={field.slug}
                  className="rounded-xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950"
                >
                  <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-neutral-950 dark:text-white">{field.name}</p>
                    <span className="max-w-full truncate rounded-md bg-neutral-50 px-2 py-1 text-[11px] text-neutral-400 dark:bg-neutral-900">
                      {field.slug}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm leading-7 text-neutral-500">
                    {getSubmissionFieldValue(detailsSubmission, field.slug)}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-200 px-4 py-10 text-center text-sm text-neutral-500 dark:border-neutral-800">
              {t('requestsNoFieldsToShow')}
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end border-t border-neutral-100 px-5 py-4 dark:border-neutral-900 sm:px-6">
          <Button type="button" variant="secondary" onClick={() => setDetailsSubmission(null)}>
            {t('close')}
          </Button>
        </div>
      </Modal>
    </>
  );
};
